import {useState, useCallback} from 'react';
import {useIntl} from 'react-intl';
import Radio from '@material-ui/core/Radio';
import Popover from '@material-ui/core/Popover';
import Link from '@material-ui/core/Link';
import moment from 'moment';
import type {HardwareTokenCertificate} from '../types';
import './CertificateRow.less';

interface CertificateRowProps {
    certificate: HardwareTokenCertificate;
    isSelected: boolean;
    onSelect: (certificate: HardwareTokenCertificate) => void;
}

const DATE_INPUT_FORMATS = [
    'ddd MMM DD HH:mm:ss [GMT] YYYY',
    moment.ISO_8601
];
const DATE_OUTPUT_FORMAT = 'DD/MM/YYYY';

function getFormattedDate(dateString: string): string {
    if (!dateString) {
        return '';
    }

    const parsedDate = moment(dateString, DATE_INPUT_FORMATS);

    if (parsedDate.isValid()) {
        return parsedDate.format(DATE_OUTPUT_FORMAT);
    }

    return dateString;
}

function getCommonNameFromDN(dn: string): string {
    if (!dn) {
        return '';
    }

    const cnMatch = dn.match(/CN=([^,]+)/i);
    return cnMatch ? cnMatch[1] : dn;
}

/**
 * Certificate card component for hardware token signing.
 * Displays certificate info with radio button selection.
 */
export function CertificateRow({
    certificate,
    isSelected,
    onSelect
}: CertificateRowProps): JSX.Element {
    const {formatMessage} = useIntl();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleCardClick = useCallback(() => {
        onSelect(certificate);
    }, [certificate, onSelect]);

    const handleDetailsClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    }, []);

    const handlePopoverClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const isPopoverOpen = Boolean(anchorEl);
    const subjectCN = getCommonNameFromDN(certificate.subject);
    const issuerCN = getCommonNameFromDN(certificate.issuer);
    const expirationDate = getFormattedDate(certificate.validTo);
    const validFromDate = getFormattedDate(certificate.validFrom);
    const displayName = certificate.alias || subjectCN;

    return (
        <>
            <div
                className={`certificate-card ${isSelected ? 'certificate-card--selected' : ''}`}
                onClick={handleCardClick}
                data-wdio="test-certificate-row"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleCardClick();
                    }
                }}
            >
                <div className="certificate-card-header">
                    <Radio
                        checked={isSelected}
                        inputProps={{'aria-label': displayName}}
                        className="certificate-card-radio"
                    />
                    <span className="certificate-card-name">{displayName}</span>
                </div>
                <div className="certificate-card-details">
                    <div className="certificate-card-field">
                        <span className="certificate-card-label">
                            {formatMessage({
                                id: 'oss.pcc.CertificateList.Column.issuedTo',
                                defaultMessage: 'Issued To'
                            })}
                        </span>
                        <span className="certificate-card-value">{subjectCN}</span>
                    </div>
                    <div className="certificate-card-field">
                        <span className="certificate-card-label">
                            {formatMessage({
                                id: 'oss.pcc.CertificateList.Column.issuedBy',
                                defaultMessage: 'Issued By'
                            })}
                        </span>
                        <span className="certificate-card-value">{issuerCN}</span>
                    </div>
                    <div className="certificate-card-field">
                        <span className="certificate-card-label">
                            {formatMessage({
                                id: 'oss.pcc.CertificateList.Column.expiration',
                                defaultMessage: 'Expiration'
                            })}
                        </span>
                        <span className="certificate-card-value">{expirationDate}</span>
                    </div>
                </div>
                <div className="certificate-card-actions">
                    <Link
                        component="button"
                        onClick={handleDetailsClick}
                        data-wdio="test-certificate-details-button"
                    >
                        {formatMessage({
                            id: 'oss.pcc.CertificateList.Column.Action.details',
                            defaultMessage: 'See Details'
                        })}
                    </Link>
                </div>
            </div>
            <Popover
                open={isPopoverOpen}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <div className="certificate-details-popover">
                    <h4 className="certificate-details-popover-title">
                        {formatMessage({
                            id: 'oss.pcc.CertificateDetails.title',
                            defaultMessage: 'Certificate Details'
                        })}
                    </h4>
                    <dl className="certificate-details-popover-list">
                        <div className="certificate-details-popover-item">
                            <dt>
                                {formatMessage({
                                    id: 'oss.pcc.CertificateDetails.Field.sn',
                                    defaultMessage: 'SN'
                                })}
                            </dt>
                            <dd>{certificate.serial}</dd>
                        </div>
                        <div className="certificate-details-popover-item">
                            <dt>
                                {formatMessage({
                                    id: 'oss.pcc.CertificateDetails.Field.subject',
                                    defaultMessage: 'Subject'
                                })}
                            </dt>
                            <dd>{certificate.subject}</dd>
                        </div>
                        <div className="certificate-details-popover-item">
                            <dt>
                                {formatMessage({
                                    id: 'oss.pcc.CertificateDetails.Field.issuer',
                                    defaultMessage: 'Issuer'
                                })}
                            </dt>
                            <dd>{certificate.issuer}</dd>
                        </div>
                        <div className="certificate-details-popover-item">
                            <dt>
                                {formatMessage({
                                    id: 'oss.pcc.CertificateDetails.Field.validFrom',
                                    defaultMessage: 'Valid From'
                                })}
                            </dt>
                            <dd>{validFromDate}</dd>
                        </div>
                        <div className="certificate-details-popover-item">
                            <dt>
                                {formatMessage({
                                    id: 'oss.pcc.CertificateDetails.Field.validTo',
                                    defaultMessage: 'Expiration Date'
                                })}
                            </dt>
                            <dd>{expirationDate}</dd>
                        </div>
                        {certificate.algorithm && (
                            <div className="certificate-details-popover-item">
                                <dt>
                                    {formatMessage({
                                        id: 'oss.pcc.CertificateDetails.Field.algorithm',
                                        defaultMessage: 'Algorithm'
                                    })}
                                </dt>
                                <dd>{certificate.algorithm}</dd>
                            </div>
                        )}
                    </dl>
                </div>
            </Popover>
        </>
    );
}
